/**
 * Gestionnaire pour les actions des ActionCards
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import waproxyClient from '../utils/waproxy-calls.js';
import { runTemplate, calculateTemplateKey, getTemplateData } from './handlebarIA-services.js';
import { isHandlebarsInitialized } from './handlebarsHelpers.js';
import { MAIN_CLASS, PATH_AXIS } from './wa-item-class.js';
import { callIA } from './ia-services.js';
import { json2Manuscript, manuscript2JSON, json2Article, text2Article } from './content-structure-services.js';
import { convertCanonText } from './bbcodeHelpers.js'

// Constantes pour les actions des boutons
export const BUTTON_ACTION = {
  PROCESS: 'PROCESS',
  COPY: 'COPY',
  FLAG: 'FLAG'
};

/** rappel des structures de données
 *
 * action :
 *      "function_tag": "isBeat",
 *      "task_icon": "REWRITE",
 *      "task_name": "BeatReWriter",
 *      "works_on": "text",
 *      "needs": "Parent",
 *      "link": "Block",
 *      "entity_class": "Dungeons & Dragons 5e",
 *      "link_entity_class": "relatedArticlesSpecies",
 *      "use_ai": "text",
 *      "use_canon": 1,
 *      "data_format": "html",
 *      "post_ia_treatment": "isCharacter>Canon",
 *      "article_field_identification": "isDivinity>Article",
 *      "tag_set_action": "Canon",
 *
 * waItems :
 *      mainWaItem: actionCard.mainWaItem,
 *      associatedWaItem: actionCard.associatedWaItem,
 *      additionalWaItems: waItemGroups.map(g => g.waItem)
 * 
 * WaItem :
 *      role: "Parent"
 *      isMain: false
 *      mainClass: "ManuscriptPart"
 *      id: "6f486dd7-0389-4f06-8c8f-18f1b4bf994c"
 *      isLoaded: true
 *      data:
 *          _mainClass: "ManuscriptPart"
 *          _class: "folder"
 *          id: "6f486dd7-0389-4f06-8c8f-18f1b4bf994c"
 *          title: "C5 - Douce nuit à Luskan"
 *          slug: null
 *          state: null
 *          entityClass: "ManuscriptPart"
 *          ...
 */


/**
 * Récupère tous les contenus des manuscripts canon (via le label canon)
 * @returns {Promise<Array>} - Tableau contenant les objets { content } des manuscripts canon
 */
const getCanonManuscripts = async () => {
  const toReturn = [];
  
  try {
    const waLabelCanon = JSON.parse(localStorage.getItem('wa_label_canon') || '{}');
    const canonLabelId = waLabelCanon.id;
    
    if (!canonLabelId) {
      debug.error(config, 'getCanonManuscripts: Aucun label canon trouvé dans localStorage');
      return toReturn;
    }
    
    debug.log(config, 'getCanonManuscripts: Récupération des manuscripts canon');
    
    const theCanonManuscripts = await waproxyClient.waGet('/enablecache/manuscript_label', canonLabelId, 2);
    
    if (!theCanonManuscripts || !theCanonManuscripts.parts) {
      debug.log(config, 'getCanonManuscripts: Aucune partie trouvée');
      return toReturn;
    }
    
    for (const part of theCanonManuscripts.parts) {
      try {
        const theCanonPart = await waproxyClient.waGet('/enablecache/manuscript_part', part.id, 2);
        if (theCanonPart && theCanonPart.content) {
          const convertedContent = await convertCanonText(theCanonPart.content);
          toReturn.push({ content: convertedContent });
          debug.log(config, `getCanonManuscripts: Contenu récupéré pour part ${part.id}`);
        }
      } catch (error) {
        debug.error(config, `getCanonManuscripts: Erreur pour part ${part.id}:`, error);
      }
    }
  } catch (error) {
    debug.error(config, 'getCanonManuscripts: Erreur générale:', error);
  }
  
  debug.log(config, `getCanonManuscripts: ${toReturn.length} éléments récupérés`);
  return toReturn;
};

/**
 * Récupère le scrapbook d'un article relié comme donnée canon
 * @param {string} articleId - ID de l'article à récupérer
 * @returns {Promise<Object|null>} - Objet { content } ou null si pas de scrapbook
 */
const getRelatedArticleScrapbook = async (articleId) => {
  try {
    debug.log(config, `getRelatedArticleScrapbook: Récupération du scrapbook pour ${articleId}`);
    const article = await waproxyClient.waGet('/enablecache/article', articleId, 2);
    if (article && article.scrapbook) {
      const convertedContent = await convertCanonText(article.scrapbook);
      debug.log(config, `getRelatedArticleScrapbook: Scrapbook trouvé pour ${articleId}`);
      return { content: convertedContent };
    }
    debug.log(config, `getRelatedArticleScrapbook: Aucun scrapbook pour ${articleId}`);
  } catch (error) {
    debug.error(config, `getRelatedArticleScrapbook: Erreur pour article ${articleId}:`, error);
  }
  return null;
};

/**
 * Prépare les données des axes relatifs basées sur les templates
 * @param {Object} action - Structure de l'action
 * @param {Object} waItems - WaItems disponibles
 * @param {string} agent - Agent du template (par défaut null)
 * @returns {Promise<Object>} - Objet contenant les données des axes trouvés
 */
const prepareTemplateRun = async (action, waItems, agent = null) => {
  const template = await getTemplateData(action, agent);
  if (!template || !template.is_valid) {
    debug.log(config, 'prepareTemplateRun: Aucun template trouvé ou template sans source');
    return {};
  }

  const axisData = {};
  
  // Si le template n'a pas d'axes nécessaires, retourner un objet vide
  if (!template.needed_axis) {
    debug.log(config, 'prepareTemplateRun: Aucun axe nécessaire pour ce template');
    return axisData;
  }
  
  // Boucler sur les axes spécifiés dans needed_axis
  const neededAxes = template.needed_axis.split(',').map(axis => axis.trim());
  
  for (const axisKey of neededAxes) {
    debug.log(config, `prepareTemplateRun: Traitement de l'axe: ${axisKey}`);
    
    try {
      // Extraire la valeur de l'axe (enlever le suffix "Part")
      const axisValue = axisKey.replace('Part', '');
      
      // Appeler readRelativePartData pour l'axe correspondant
      const relativeData = await waItems.mainWaItem.readRelativePartData(axisValue);
      axisData[axisKey] = relativeData;
      debug.log(config, `prepareTemplateRun: Données récupérées pour ${axisKey}:`, relativeData?.length || 'données uniques');
    } catch (error) {
      debug.error(config, `prepareTemplateRun: Erreur lors de la récupération des données pour ${axisKey}:`, error);
      axisData[axisKey] = null;
    }
  }
  
  debug.log(config, 'prepareTemplateRun: Données préparées:', Object.keys(axisData));
  return axisData;
};

/**
 * Fonction principale pour gérer les actions des ActionCards
 * @param {string} buttonAction - Action du bouton (PROCESS, COPY, FLAG)
 * @param {Object} action - Structure de l'action
 * @param {Object} waItems - WaItems disponibles (mainWaItem et additionalWaItems)
 * @param {Object} loadingContext - Contexte de loading (startLoading, stopLoading)
 * @returns {Promise<void>} - Une promesse qui se résout lorsque l'action est terminée
 */
export const doActionCard = async (buttonAction, action, waItems, loadingContext = {}, workingLanguage = 'FR') => {
  const { startLoading, stopLoading, showSuccess, updateLoadingMessage } = loadingContext;
  
  debug.log(config, 'ActionCardHandler: doActionCard appelée:', {
    buttonAction,
    action: {
      task_name: action.task_name,
      function_tag: action.function_tag,
      works_on: action.works_on,
      needs: action.needs,
      link: action.link,
      link_entity_class: action.link_entity_class,
      entity_class: action.entity_class,
      use_ai: action.use_ai,
      use_canon: action.use_canon,
      data_format: action.data_format,
      post_ia_treatment: action.post_ia_treatment,
      article_field_identification: action.article_field_identification,
      tag_set_action: action.tag_set_action
    },
    waItems: {
      mainWaItem: waItems.mainWaItem?.id,
      associatedWaItem: waItems.associatedWaItem?.id,
      additionalWaItems: waItems.additionalWaItems?.length || 0
    },
    loadingContext: {
      hasStartLoading: !!startLoading,
      hasStopLoading: !!stopLoading,
      hasShowSuccess: !!showSuccess,
      hasUpdateLoadingMessage: !!updateLoadingMessage
    }
  });

  // Démarrer le loading
  if (startLoading) {
    debug.log(config, 'ActionCardHandler: Appel de startLoading');
    startLoading(`Traitement de l'action: ${action.task_name}`, 'info');
  } else {
    debug.warn(config, 'ActionCardHandler: startLoading non disponible dans loadingContext');
  }

  try {
    switch (buttonAction) {
      case BUTTON_ACTION.PROCESS:
        await handleProcessAction(action, waItems, updateLoadingMessage, workingLanguage);
        break;
      
      case BUTTON_ACTION.COPY:
        await handleCopyAction(action, waItems, updateLoadingMessage, workingLanguage);
        break;
      
      case BUTTON_ACTION.FLAG:
        await handleFlagAction(action, waItems, updateLoadingMessage, workingLanguage);
        break;
      
      default:
        debug.error(config, 'ActionCardHandler: Action non reconnue:', buttonAction);
        break;
    }
    
    // Afficher le message de succès à la fin du traitement
    if (showSuccess) {
      debug.log(config, 'ActionCardHandler: Appel de showSuccess');
      showSuccess('Données mises à jour avec succès', true, 3000); 
    } else {
      debug.warn(config, 'ActionCardHandler: showSuccess non disponible dans loadingContext');
    }
  } catch (error) {
    debug.error(config, 'ActionCardHandler: Erreur lors du traitement de l\'action:', error);
    throw error; // Propagation de l'erreur pour gestion à un niveau supérieur
  } finally {
    // Arrêter le loading dans tous les cas
    if (stopLoading) {
      debug.log(config, 'ActionCardHandler: Appel de stopLoading');
      stopLoading();
    } else {
      debug.warn(config, 'ActionCardHandler: stopLoading non disponible dans loadingContext');
    }
  }
};

/**
 * Gère l'action PROCESS
 * @param {Object} action - Structure de l'action
 * @param {Object} waItems - WaItems disponibles
 * @param {Function} updateLoadingMessage - Fonction pour mettre à jour le message de chargement
 * @returns {Promise<void>} - Une promesse qui se résout lorsque l'action est terminée
 */
const handleProcessAction = async (action, waItems, updateLoadingMessage, workingLanguage = 'FR') => {
  // Variables
  let FromTextSystemResult=null;
  let FromTextAssistantResult=null;
  let FromTextUserResult=null;
  let FromURL=null;

  // vérification de la présence de loadingcontext
  if (!updateLoadingMessage) {
    debug.error(config, 'ActionCardHandler: updateLoadingMessage non disponible');
  }

  /** 
   * ------- reading data from world using the templates
   */
  if (updateLoadingMessage) {
    updateLoadingMessage('Application des modèles system, user, assistant et url', 'info');
  }

  // Préparer les données des axes en parallèle
  const [systemAxisData, userAxisData, assistantAxisData, urlAxisData] = await Promise.all([
    prepareTemplateRun(action, waItems, "@system"),
    prepareTemplateRun(action, waItems, "@user"),
    prepareTemplateRun(action, waItems, "@assistant"),
    prepareTemplateRun(action, waItems, "@url")
  ]);

  // Exécuter les templates en parallèle
  const [systemResult, userResult, assistantResult, urlResult] = await Promise.all([
    runTemplate(action, { 
      action: action, 
      article: (waItems.associatedWaItem.mainClass !== MAIN_CLASS.MANUSCRIPT_PART) ? waItems.associatedWaItem.data : null,
      part: waItems.mainWaItem.data, 
      ...systemAxisData
    }, "@system", workingLanguage),
    runTemplate(action, { 
      action: action, 
      article: (waItems.associatedWaItem.mainClass !== MAIN_CLASS.MANUSCRIPT_PART) ? waItems.associatedWaItem.data : null,
      part: waItems.mainWaItem.data, 
      ...userAxisData
    }, "@user", workingLanguage),
    runTemplate(action, { 
      action: action, 
      article: (waItems.associatedWaItem.mainClass !== MAIN_CLASS.MANUSCRIPT_PART) ? waItems.associatedWaItem.data : null,
      part: waItems.mainWaItem.data, 
      ...assistantAxisData
    }, "@assistant", workingLanguage),
    runTemplate(action, { 
      action: action, 
      article: (waItems.associatedWaItem.mainClass !== MAIN_CLASS.MANUSCRIPT_PART) ? waItems.associatedWaItem.data : null,
      part: waItems.mainWaItem.data, 
      ...urlAxisData
    }, "@url", workingLanguage)
  ]);

  // Extraire les résultats text
  FromTextSystemResult = systemResult.text;
  FromTextUserResult = userResult.text;
  FromTextAssistantResult = assistantResult.text;
  FromURL = urlResult.text;

  /** 
   * ------- calling the IA if required
  */
  // note : le traitement url non inclus pour le moment
  // Build message stack for AI call
  const theMessagesMain = [];
  let aiResponse = { data: FromTextUserResult }; // par défaut, si pas d'IA, on wrappe le texte user
  
  if (action.use_ai) {
    if (updateLoadingMessage) {
      updateLoadingMessage(`Lecture des canons`, 'info');
    }

    // Pré-charger les données de template pour les trois agents
    const systemTemplateData = await getTemplateData(action, "@system");
    const assistantTemplateData = await getTemplateData(action, "@assistant");
    const userTemplateData = await getTemplateData(action, "@user");

    // Initialisation des données canon
    const theCanonData = {};
    
    // Récupération des données canon si nécessaire
    if (action.use_canon === 1) {
      if (updateLoadingMessage) {
        updateLoadingMessage('Récupération des données canon', 'info');
      }
      
      // Source 1 : Manuscripts canon (via le label canon)
      if (updateLoadingMessage) {
        updateLoadingMessage('Récupération des manuscripts canon', 'info');
      }
      theCanonData.manuscripts = await getCanonManuscripts();
      
      // Source 2 : Scrapbooks des articles reliés
      if (updateLoadingMessage) {
        updateLoadingMessage('Récupération des scrapbooks des articles reliés', 'info');
      }
      
      const ignoredIds = [];
      if (waItems.associatedWaItem?.data?.id) {
        ignoredIds.push(waItems.associatedWaItem.data.id);
      }
      
      /**
       * Collecte les scrapbooks des articles reliés à un objet data
       * @param {Object} data - L'objet data contenant les relations
       */
      const collectRelatedScrapbooks = async (data) => {
        if (!data) return;
        
        // povCharacter
        if (data.povCharacter?.id && !ignoredIds.includes(data.povCharacter.id)) {
          if (!theCanonData.povCharacter) theCanonData.povCharacter = [];
          const scrapbook = await getRelatedArticleScrapbook(data.povCharacter.id);
          if (scrapbook) theCanonData.povCharacter.push(scrapbook);
          ignoredIds.push(data.povCharacter.id);
        }
        
        // relatedCharacters
        if (Array.isArray(data.relatedCharacters)) {
          if (!theCanonData.relatedCharacters) theCanonData.relatedCharacters = [];
          for (const character of data.relatedCharacters) {
            if (character.id && !ignoredIds.includes(character.id)) {
              const scrapbook = await getRelatedArticleScrapbook(character.id);
              if (scrapbook) theCanonData.relatedCharacters.push(scrapbook);
              ignoredIds.push(character.id);
            }
          }
        }
        
        // relatedLocations
        if (Array.isArray(data.relatedLocations)) {
          if (!theCanonData.relatedLocations) theCanonData.relatedLocations = [];
          for (const location of data.relatedLocations) {
            if (location.id && !ignoredIds.includes(location.id)) {
              const scrapbook = await getRelatedArticleScrapbook(location.id);
              if (scrapbook) theCanonData.relatedLocations.push(scrapbook);
              ignoredIds.push(location.id);
            }
          }
        }
        
        // relatedItems
        if (Array.isArray(data.relatedItems)) {
          if (!theCanonData.relatedItems) theCanonData.relatedItems = [];
          for (const item of data.relatedItems) {
            if (item.id && !ignoredIds.includes(item.id)) {
              const scrapbook = await getRelatedArticleScrapbook(item.id);
              if (scrapbook) theCanonData.relatedItems.push(scrapbook);
              ignoredIds.push(item.id);
            }
          }
        }
        
        // relatedArticles
        if (Array.isArray(data.relatedArticles)) {
          if (!theCanonData.relatedArticles) theCanonData.relatedArticles = [];
          for (const article of data.relatedArticles) {
            if (article.id && !ignoredIds.includes(article.id)) {
              const scrapbook = await getRelatedArticleScrapbook(article.id);
              if (scrapbook) theCanonData.relatedArticles.push(scrapbook);
              ignoredIds.push(article.id);
            }
          }
        }
      };
      
      // Traitement des articles reliés au mainWaItem
      if (waItems.mainWaItem?.data) {
        await collectRelatedScrapbooks(waItems.mainWaItem.data);
      }
      
      // Si associatedWaItem.role = "Parent", traiter aussi les relations de associatedWaItem
      if (waItems.associatedWaItem?.role === "Parent" && waItems.associatedWaItem?.data) {
        await collectRelatedScrapbooks(waItems.associatedWaItem.data);
      }
      
      debug.log(config, 'handleProcessAction: theCanonData:', theCanonData);

      // Exécuter les templates CANON en parallèle
      const [systemCanonResult, userCanonResult, assistantCanonResult] = await Promise.all([
        runTemplate(null, { 
           ...theCanonData
        }, "CanonTemplate@system", workingLanguage),
        runTemplate(null, { 
          ...theCanonData
        }, "CanonTemplate@user", workingLanguage),
        runTemplate(null, { 
          ...theCanonData
        }, "CanonTemplate@assistant", workingLanguage)
      ]);
 
      // Ajout des messages au stack : from template results
      if (systemCanonResult.text && systemTemplateData) {
        theMessagesMain.push( {	IA : systemTemplateData.use,
                                 role : "system", 
                                 temperature : systemTemplateData.temperature,
                                 text : systemCanonResult.text,
                                 url : null } );
        debug.log(config, 'ActionCardHandler: Message ajouté au stack (system)');
      }

      if (assistantCanonResult.text && assistantTemplateData) {
        theMessagesMain.push( { IA : assistantTemplateData.use,
                                 role : "assistant", 
                                 temperature : assistantTemplateData.temperature,
                                 text : assistantCanonResult.text,
                                 url : null } );
        debug.log(config, 'ActionCardHandler: Message ajouté au stack (assistant)');
      }

      if (userCanonResult.text && userTemplateData) {
        theMessagesMain.push( { IA : userTemplateData.use,
                                 role : "user", 
                                 temperature : userTemplateData.temperature,
                                 text : userCanonResult.text,
                                 url : null } );
        debug.log(config, 'ActionCardHandler: Message ajouté au stack (user)');
      }
    }

    if (updateLoadingMessage) {
      updateLoadingMessage(`Appel de l'IA`, 'info');
    }

    // Ajout des messages au stack : from template results
    if (FromTextSystemResult && systemTemplateData) {
		theMessagesMain.push( {	IA : systemTemplateData.use,
														 	role : "system", 
														 	temperature : systemTemplateData.temperature,
														 	text : FromTextSystemResult,
														 	url : null } );
      debug.log(config, 'ActionCardHandler: Message ajouté au stack (system)');
	}

	if (FromTextAssistantResult && assistantTemplateData) {
		theMessagesMain.push( { IA : assistantTemplateData.use,
														 	role : "assistant", 
														 	temperature : assistantTemplateData.temperature,
														 	text : FromTextAssistantResult,
														 	url : null } );
      debug.log(config, 'ActionCardHandler: Message ajouté au stack (assistant)');
	}
	
	if (FromTextUserResult && userTemplateData) {
		theMessagesMain.push( { IA : userTemplateData.use,
														 	role : "user", 
														 	temperature : userTemplateData.temperature,
														 	text : FromTextUserResult,
														 	url : FromURL } );
      debug.log(config, 'ActionCardHandler: Message ajouté au stack (user)');
    }
    debug.log(config, 'ActionCardHandler: Stack des messages pour IA:', theMessagesMain);

    // Appel de l'IA
    aiResponse = await callIA(theMessagesMain);
  }


  /**
   * ------- post-ia-treatment
   */
  let theResponseMain = aiResponse.data;
  if (action.use_ai && action.post_ia_treatment) {
    if (updateLoadingMessage) {
      updateLoadingMessage(`Traitement post-IA ${action.post_ia_treatment}`, 'info');
    }
    theResponseMain = await json2Manuscript(await manuscript2JSON(aiResponse.data, action.post_ia_treatment, workingLanguage));
  }
 
  /**
   * ------- traitement avec modele output (corps)
   */
  if (updateLoadingMessage) {
    updateLoadingMessage(`Formatage sortie`, 'info');
  }
  
  const outputAxisData = await prepareTemplateRun(action, waItems, "/outputcontent");
  let OutputContent = await runTemplate(action, { 
    action: action, 
    article: (waItems.associatedWaItem.mainClass === MAIN_CLASS.ARTICLE) ? waItems.associatedWaItem.data : null,
    part: waItems.mainWaItem.data, 
    ...outputAxisData,
    output: { content: theResponseMain }
  }, "/outputcontent", workingLanguage);

  /**
   * ------- post-output article_field_identification treatment
   */
  if (action.article_field_identification && action.article_field_identification !== action.post_ia_treatment) {
    if (updateLoadingMessage) {
      updateLoadingMessage(`Traitement post-IA ${action.article_field_identification}`, 'info');
    }
    OutputContent = { ...OutputContent, text: await json2Manuscript(await manuscript2JSON(OutputContent.text, action.article_field_identification, workingLanguage)) };
  }

  /** 
   * ------- ecriture dans World Anvil
   */
  if (updateLoadingMessage) {
    updateLoadingMessage(`Ecriture dans le Manuscript World Anvil`, 'info');
  }
 
  const waLabelGenerated = JSON.parse(localStorage.getItem('wa_label_generated') || '{}');
  const waJSONQuery = {
    content : OutputContent.text,
    label: {
      id: waLabelGenerated.id || null
    }
  }
  debug.log(config, 'handleProcessAction: waJSONQuery à écrire :', waJSONQuery);
  
  await waproxyClient.waPatch('/manuscript_part', waItems.mainWaItem.id, waJSONQuery);
};



/**
 * Gère l'action COPY
 * @param {Object} action - Structure de l'action
 * @param {Object} waItems - WaItems disponibles
 * @returns {Promise<void>} - Une promesse qui se résout lorsque l'action est terminée
 */
const handleCopyAction = async (action, waItems, updateLoadingMessage, workingLanguage = 'FR') => {

  // vérification de la présence de loadingcontext
  if (!updateLoadingMessage) {
      debug.error(config, 'handleCopyAction : updateLoadingMessage non disponible');
  }

  if (updateLoadingMessage) {
    updateLoadingMessage(`Copie des données du manuscript`, 'info');
  }

  /**
   * ------- applique le template de copy
   */
  if (updateLoadingMessage) {
    updateLoadingMessage(`Application du template de copie`, 'info');
  }

  // Préparer les données des axes relatifs basées sur le template de copy
  const copyAxisData = await prepareTemplateRun(action, waItems, "/copy");

  let FromCopyResult = await runTemplate(action, { 
    action: action, 
    article: (waItems.associatedWaItem.mainClass === MAIN_CLASS.ARTICLE) ? waItems.associatedWaItem.data : null,
    part: waItems.mainWaItem.data, 
    ...copyAxisData
  }, "/copy", workingLanguage);

  /**
   * ------- copie dans l'article dans World Anvil
   */
  if (!FromCopyResult.text) {
    debug.error(config, 'handleCopyAction: Aucune donnée à copier dans l\'article');
    return;
  }

  if (updateLoadingMessage) {
    updateLoadingMessage(`Copie dans l'article`, 'info');
  }

  debug.log(config, 'handleCopyAction: article_field_identification:', action.article_field_identification);

  let waJSONQuery={};
  let manuscriptContent = null;
  if (!action.article_field_identification ) {
    waJSONQuery = await text2Article(FromCopyResult.text);
  } else {
    const theJSON = await manuscript2JSON(FromCopyResult.text, action.article_field_identification, workingLanguage);
    manuscriptContent = await json2Manuscript(theJSON);
    const fieldType = action.article_field_identification.split('>')[1];
    debug.log(config, 'handleCopyAction: Type de champ:', fieldType);

    switch(fieldType) {
      case 'Article':
      case 'Block':
          // se ramener à la structure de l'article
        waJSONQuery = await json2Article(theJSON);
        break;
      default:
        debug.error(config, 'handleCopyAction: article_field_identification non reconnu:', action.article_field_identification);
    }
  }

  waItems.associatedWaItem.patchData(waJSONQuery); 

  /**
   * ------- mise à jour états des manuscripts dans World Anvil
   */
  // label 
  const waLabelGenerated = JSON.parse(localStorage.getItem('wa_label_copied') || '{}');
  const waJSONQueryManuscript = {
    ...(manuscriptContent ? { content: manuscriptContent } : {}),
    label: {
      id: waLabelGenerated.id || null
    }
  };

  // Boucle sur FromCopyResult.metadata.childrenId et mise à jour de chaque enfant
  if (FromCopyResult && FromCopyResult.metadata && FromCopyResult.metadata.childrenId) {
    for (const id of FromCopyResult.metadata.childrenId) {
      try {
        if (updateLoadingMessage) {
          updateLoadingMessage(`Mise à jour états des manuscripts enfants`, 'info');
        }
        await waproxyClient.waPatch('/manuscript_part', id, waJSONQueryManuscript);
        debug.log(config, `handleCopyAction: Manuscript enfant mis à jour: ${id}`);
      } catch (error) {
        debug.error(config, `handleCopyAction: Erreur lors de la mise à jour du manuscript enfant ${id}:`, error);
      }
    }
  }

  // Mise à jour du manuscript principal
  if (updateLoadingMessage) {
    updateLoadingMessage(`Mise à jour état du manuscript`, 'info');
  }
  await waproxyClient.waPatch('/manuscript_part', waItems.mainWaItem.id, waJSONQueryManuscript);
};


/**
 * Gère l'action FLAG
 * @param {Object} action - Structure de l'action
 * @param {Object} waItems - WaItems disponibles
 * @param {Object} loadingContext - Contexte de loading (startLoading, stopLoading)
 * @returns {Promise<void>} - Une promesse qui se résout lorsque l'action est terminée
 */
const handleFlagAction = async (action, waItems, updateLoadingMessage, workingLanguage = 'FR') => {

  // vérification de la présence de loadingcontext
  if (!updateLoadingMessage) {
    debug.error(config, 'handleFlagAction: updateLoadingMessage non disponible');
  }

  if (updateLoadingMessage) {
    updateLoadingMessage(`Application du tag ${action.tag_set_action}`, 'info');
  }

  /**
   * ------- applique le flag
   */
  // Switch sur action.tag_set_action pour déterminer quel label utiliser
  let waLabel = null;
  switch (action.tag_set_action) {
    case "Canon":
      waLabel = JSON.parse(localStorage.getItem('wa_label_canon') || '{}');
      break;
      
    case "Generated":
      waLabel = JSON.parse(localStorage.getItem('wa_label_generated') || '{}');
      break;
      
    case "Copied":
      waLabel = JSON.parse(localStorage.getItem('wa_label_copied') || '{}');
      break;
      
    default:
      debug.error(config, 'handleFlagAction: Tag set action non reconnue ou vide:', action.tag_set_action);
      return; // Sortie anticipée en cas d'erreur
  }

  // Mise à jour du tag dans World Anvil
  const waJSONQuery = {
    label: {
      id: waLabel.id || null
    }
  };
  
  await waproxyClient.waPatch('/manuscript_part', waItems.mainWaItem.id, waJSONQuery);
};

